import { useState } from 'react';
import { X, Mail, Loader2, CheckCircle, Copy, ExternalLink } from 'lucide-react';
import { useCreateInvitation, useConnectedAccountsDetail } from '../api/hooks';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
}

export function InviteMemberModal({ isOpen, onClose, memberId, memberName }: InviteMemberModalProps) {
  const [email, setEmail] = useState('');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const createInvitation = useCreateInvitation();
  const { data: accountDetails } = useConnectedAccountsDetail(memberId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      const result = await createInvitation.mutateAsync({ memberId, email: email.trim() });
      setEmailSent(result.emailSent);
      if (result.inviteUrl) {
        setInviteUrl(result.inviteUrl);
      } else {
        // Email was sent successfully
        setInviteUrl(null);
      }
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const handleCopyLink = async () => {
    if (inviteUrl) {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setEmail('');
    setInviteUrl(null);
    setEmailSent(false);
    setCopied(false);
    createInvitation.reset();
    onClose();
  };

  if (!isOpen) return null;

  // Success state - invitation created
  if (createInvitation.isSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b border-border-default">
            <h2 className="text-lg font-semibold text-text-primary">Invitation Sent</h2>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-text-secondary" />
            </button>
          </div>

          <div className="p-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              {emailSent ? (
                <>
                  <h3 className="text-lg font-medium text-text-primary mb-2">Email Sent!</h3>
                  <p className="text-text-secondary">
                    We've sent an invitation email to <span className="font-medium">{email}</span>.
                    They can click the link in the email to join as {memberName}.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-text-primary mb-2">Invitation Created</h3>
                  <p className="text-text-secondary mb-4">
                    Email service is not configured. Share this link with {memberName} to let them join:
                  </p>
                  {inviteUrl && (
                    <div className="bg-bg-secondary rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={inviteUrl}
                          readOnly
                          className="flex-1 bg-transparent text-sm text-text-primary truncate outline-none"
                        />
                        <button
                          onClick={handleCopyLink}
                          className="flex items-center gap-1 px-3 py-1.5 bg-teal text-white rounded-lg text-sm font-medium hover:bg-teal/90 transition-colors"
                        >
                          {copied ? (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <button
              onClick={handleClose}
              className="btn btn-primary w-full"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-border-default">
          <h2 className="text-lg font-semibold text-text-primary">Invite {memberName}</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Connected Accounts Status */}
        {accountDetails && (
          <div className="p-4 border-b border-border-default bg-bg-secondary">
            <h3 className="text-sm font-medium text-text-primary mb-2">Connected Accounts</h3>

            {/* Google Accounts */}
            <div className="mb-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-text-primary">Google Calendar</span>
                <span className="text-xs text-text-secondary">
                  ({accountDetails.google.count}/{accountDetails.google.max})
                </span>
              </div>
              {accountDetails.google.accounts.length > 0 ? (
                <div className="space-y-1">
                  {accountDetails.google.accounts.map((account, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-text-secondary">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      <span>{account.email}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-text-secondary">No accounts connected</p>
              )}
            </div>

            {/* Microsoft Accounts */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-text-primary">Microsoft Calendar</span>
                <span className="text-xs text-text-secondary">
                  ({accountDetails.microsoft.count}/{accountDetails.microsoft.max})
                </span>
              </div>
              {accountDetails.microsoft.accounts.length > 0 ? (
                <div className="space-y-1">
                  {accountDetails.microsoft.accounts.map((account, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-text-secondary">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      <span>{account.email}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-text-secondary">No accounts connected</p>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6">
          <p className="text-text-secondary mb-4">
            {accountDetails ? (
              <>
                {accountDetails.google.count === 0 && accountDetails.microsoft.count === 0 ? (
                  `Send an invitation to let ${memberName} create an account and connect their calendars.`
                ) : (
                  <>
                    {accountDetails.google.count < accountDetails.google.max && accountDetails.microsoft.count < accountDetails.microsoft.max ? (
                      `Invite ${memberName} to connect additional Google or Microsoft accounts.`
                    ) : accountDetails.google.count < accountDetails.google.max ? (
                      `Invite ${memberName} to connect an additional Google account (${accountDetails.google.max - accountDetails.google.count} slot available).`
                    ) : accountDetails.microsoft.count < accountDetails.microsoft.max ? (
                      `Invite ${memberName} to connect an additional Microsoft account (${accountDetails.microsoft.max - accountDetails.microsoft.count} slot available).`
                    ) : (
                      `${memberName} has reached the maximum number of connected accounts.`
                    )}
                  </>
                )}
              </>
            ) : (
              `Send an invitation to let ${memberName} connect their calendars.`
            )}
          </p>

          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
              Email address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter their email address"
                className="w-full pl-10 pr-4 py-2.5 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal"
                required
              />
            </div>
          </div>

          {createInvitation.isError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">
                {createInvitation.error instanceof Error
                  ? createInvitation.error.message
                  : 'Failed to send invitation'}
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createInvitation.isPending || !email.trim()}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {createInvitation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4" />
                  Send Invitation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
